import type { ComponentPropsWithRef, ReactNode } from "react";
import { DropdownMenu } from "radix-ui";
import { Check } from "lucide-react";
import { cx } from "./cx";

export const MenuRoot = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({ className, children, ...props }: ComponentPropsWithRef<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align="end"
        sideOffset={6}
        collisionPadding={8}
        className={cx("z-menu w-[236px] rounded-xl border border-border bg-bg p-1.5 text-left shadow-pop", className)}
        {...props}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

const ITEM =
  "flex w-full cursor-pointer items-start gap-[9px] rounded-md px-2 py-1.5 text-left text-fg outline-none select-none data-[highlighted]:bg-surface-2 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 [&>svg]:mt-0.5 [&>svg]:size-3.5 [&>svg]:text-muted";

export function MenuItem({ className, ...props }: ComponentPropsWithRef<typeof DropdownMenu.Item>) {
  return <DropdownMenu.Item className={cx(ITEM, className)} {...props} />;
}

export function MenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.CheckboxItem>) {
  return (
    <DropdownMenu.CheckboxItem className={cx(ITEM, "items-center py-1", className)} {...props}>
      <span
        className="grid size-3.5 flex-none place-items-center rounded-sm border-[1.5px] border-border bg-bg data-[checked=true]:border-link data-[checked=true]:bg-link"
        data-checked={props.checked === true}
      >
        <DropdownMenu.ItemIndicator>
          <Check size={10} strokeWidth={3} className="text-on-primary" aria-hidden="true" />
        </DropdownMenu.ItemIndicator>
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </DropdownMenu.CheckboxItem>
  );
}

export const MenuRadioGroup = DropdownMenu.RadioGroup;

export function MenuRadioItem({ className, children, ...props }: ComponentPropsWithRef<typeof DropdownMenu.RadioItem>) {
  return (
    <DropdownMenu.RadioItem className={cx(ITEM, "items-center", className)} {...props}>
      {children}
      <DropdownMenu.ItemIndicator className="ml-auto text-link">
        <Check size={14} strokeWidth={2} aria-hidden="true" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.RadioItem>
  );
}

export function MenuLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <DropdownMenu.Label
      className={cx("mx-2 mt-1 mb-1.5 text-xs font-semibold uppercase tracking-[0.04em] text-muted", className)}
    >
      {children}
    </DropdownMenu.Label>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1.5 h-px bg-divider" />;
}

export const MenuGroup = DropdownMenu.Group;
